<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'next/button/Button.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const GOOGLE_APPOINTMENT_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0kVtS1tq4ZxsFLiKqOnM1in9m1ZWyJUECnAiLIa_j2RUTvCv39nDyKqVJSoy98Y6CbJMPvh1oR?gv=true';

const props = defineProps({
  phase: {
    type: String,
    required: true,
    validator: value => ['pricing', 'growth'].includes(value),
  },
  track: {
    type: String,
    default: null,
    validator: value =>
      value == null || ['automate', 'quotes'].includes(value),
  },
});

const emit = defineEmits(['back', 'complete']);

const store = useStore();
const { t } = useI18n();
const saving = ref(false);

const titleKey = computed(() => {
  if (props.phase === 'pricing') return 'ONBOARDING_WIZARD.BOOK_CALL.PRICING_TITLE';
  if (props.track === 'quotes') return 'ONBOARDING_WIZARD.BOOK_CALL.QUOTES_TITLE';
  return 'ONBOARDING_WIZARD.BOOK_CALL.AUTOMATE_TITLE';
});

const descriptionKey = computed(() => {
  if (props.phase === 'pricing') {
    return 'ONBOARDING_WIZARD.BOOK_CALL.PRICING_DESCRIPTION';
  }
  if (props.track === 'quotes') {
    return 'ONBOARDING_WIZARD.BOOK_CALL.QUOTES_DESCRIPTION';
  }
  return 'ONBOARDING_WIZARD.BOOK_CALL.AUTOMATE_DESCRIPTION';
});

async function markComplete() {
  saving.value = true;
  try {
    await store.dispatch('onboarding/completePhase', {
      phase: props.phase,
      track: props.phase === 'growth' ? props.track : undefined,
    });
    emit('complete');
  } finally {
    saving.value = false;
  }
}

useRegisterOnboardingNav(() => ({
  primary: {
    label: t('ONBOARDING_WIZARD.MARK_COMPLETE'),
    loading: saving.value,
    disabled: saving.value,
    onClick: markComplete,
  },
  onBack: () => emit('back'),
  backLabel: t('ONBOARDING_WIZARD.RETURN_TO_HUB'),
}));
</script>

<template>
  <OnboardingStepLayout
    :title="t(titleKey)"
    :description="t(descriptionKey)"
    :step-label="t('ONBOARDING_WIZARD.HEADER')"
  >
    <div
      class="w-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white"
    >
      <iframe
        :src="GOOGLE_APPOINTMENT_URL"
        class="w-full h-[600px] border-0"
        :title="t('ONBOARDING_WIZARD.BOOK_CALL.IFRAME_TITLE')"
      />
    </div>
    <div class="mt-4 flex justify-end sm:hidden">
      <Button
        lg
        :is-loading="saving"
        :disabled="saving"
        :label="t('ONBOARDING_WIZARD.MARK_COMPLETE')"
        @click="markComplete"
      />
    </div>
  </OnboardingStepLayout>
</template>
